if(!customElements.get('scroll-navigation')) {
    customElements.define(
        'scroll-navigation',
        class ScrollNavigationComponent extends HTMLElement {
            constructor() {
                super();
                this.pixelsPerScroll = 500;
                this.isDragging = false;
                this.totalScrollableHeight = 0;
                this.scrollCount = 0;
                this.recalculationTimer = null;
                this.resizeTimeout = null;
                this.observer = null;
                this.headerObserver = null;
                this.orientationHandler = null;
                this.stickyOrientationHandler = null;
                this.previousScrollState = null;
                
                // Bind methods to preserve 'this' context
                this.handleScrollUp = this.handleScrollUp.bind(this);
                this.handleScrollDown = this.handleScrollDown.bind(this);
                this.handleIndicatorClick = this.handleIndicatorClick.bind(this);
                this.handleIndicatorDrag = this.handleIndicatorDrag.bind(this);
                this.updateIndicator = this.updateIndicator.bind(this);
                this.debouncedUpdateScrollSystem = this.debouncedUpdateScrollSystem.bind(this);
                this.updateStickyPosition = this.updateStickyPosition.bind(this);
                this.handleResize = this.handleResize.bind(this);
            }

            connectedCallback() {
                this.init();
            }

            disconnectedCallback() {
                this.cleanup();
            }

            init() {
                // Get selectors - hardcoded since attributes aren't used
                const containerSelector = '[id*="Slider-Gallery"]';
                const headerSelector = 'header';
                const stickyColumnSelector = '.product__column-sticky';

                // Find elements
                this.container = document.querySelector(containerSelector);
                this.scrollUpButton = this.querySelector('.nav-arrow.up-arrow');
                this.scrollDownButton = this.querySelector('.nav-arrow.down-arrow');
                this.indicatorsContainer = this.querySelector('.position-indicators');
                this.indicator = this.querySelector('.indicator');
                this.navigationPanel = this;
                this.header = document.querySelector(headerSelector);
                this.stickyColumn = document.querySelector(stickyColumnSelector);
                this.mediaList = document.querySelector('media-gallery slider-component-laurajames ul.product__media-list');

                // Early return if essential elements don't exist
                if (!this.container || !this.scrollUpButton || !this.scrollDownButton || 
                    !this.indicatorsContainer || !this.indicator) {
                    console.warn('Scroll navigation: Required elements not found');
                    return;
                }

                this.setupScrollNavigation();
                this.setupStickyPositioning();
            }

            cleanup() {
                // Remove event listeners
                if (this.scrollUpButton) {
                    this.scrollUpButton.removeEventListener('click', this.handleScrollUp);
                }
                if (this.scrollDownButton) {
                    this.scrollDownButton.removeEventListener('click', this.handleScrollDown);
                }
                if (this.indicatorsContainer) {
                    this.indicatorsContainer.removeEventListener('click', this.handleIndicatorClick);
                    this.indicatorsContainer.removeEventListener('touchstart', this.handleIndicatorClick);
                }
                if (this.indicator) {
                    this.indicator.removeEventListener('mousedown', this.handleIndicatorDrag);
                    this.indicator.removeEventListener('touchstart', this.handleIndicatorDrag);
                }
                if (this.container) {
                    this.container.removeEventListener('scroll', this.updateIndicator);
                }

                // Remove unified resize handler and stored orientation handlers
                window.removeEventListener('resize', this.handleResize);
                if (this.orientationHandler) {
                    window.removeEventListener('orientationchange', this.orientationHandler);
                }
                if (this.stickyOrientationHandler) {
                    window.removeEventListener('orientationchange', this.stickyOrientationHandler);
                }
                window.removeEventListener('scroll', this.debouncedUpdateScrollSystem);

                // Clean up observers
                if (this.observer) {
                    this.observer.disconnect();
                    this.observer = null;
                }
                if (this.headerObserver) {
                    this.headerObserver.disconnect();
                    this.headerObserver = null;
                }

                // Clear timers
                if (this.recalculationTimer) {
                    clearTimeout(this.recalculationTimer);
                }
                if (this.resizeTimeout) {
                    clearTimeout(this.resizeTimeout);
                }
            }

            setupScrollNavigation() {
                // Add event listeners
                this.scrollUpButton.addEventListener('click', this.handleScrollUp);
                this.scrollDownButton.addEventListener('click', this.handleScrollDown);
                
                this.indicatorsContainer.addEventListener('click', this.handleIndicatorClick);
                this.indicatorsContainer.addEventListener('touchstart', this.handleIndicatorClick, { passive: false });
                
                this.indicator.addEventListener('mousedown', this.handleIndicatorDrag);
                this.indicator.addEventListener('touchstart', this.handleIndicatorDrag, { passive: false });
                
                this.container.addEventListener('scroll', this.updateIndicator);
                
                // Store orientation handler for proper cleanup
                this.orientationHandler = () => {
                    // Wait for viewport to settle after orientation change
                    setTimeout(() => {
                        this.updateStickyPosition();
                        requestAnimationFrame(this.debouncedUpdateScrollSystem);
                    }, 100);
                };
                
                // Window event listeners - unified resize handler
                window.addEventListener('resize', this.handleResize, { passive: true });
                window.addEventListener('orientationchange', this.orientationHandler, { passive: true });
                window.addEventListener('scroll', this.debouncedUpdateScrollSystem, { passive: true });
                
                // Setup intersection observer
                if ('IntersectionObserver' in window) {
                    this.observer = new IntersectionObserver((entries) => {
                        entries.forEach(entry => {
                            if (entry.target === this.container) {
                                requestAnimationFrame(this.debouncedUpdateScrollSystem);
                            }
                        });
                    }, {
                        threshold: [0, 0.1, 0.5, 1],
                        rootMargin: '50px'
                    });
                    
                    this.observer.observe(this.container);
                }

                // Initialize
                this.updateScrollSystem();
            }

            setupStickyPositioning() {
                if (this.header && this.stickyColumn && this.mediaList) {
                    // Watch for header data-scroll-position attribute changes
                    this.headerObserver = new MutationObserver(() => {
                        requestAnimationFrame(this.updateStickyPosition);
                    });
                    this.headerObserver.observe(this.header, { 
                        attributes: true, 
                        attributeFilter: ['data-scroll-position'] 
                    });
                    
                    // Store sticky orientation handler for proper cleanup
                    this.stickyOrientationHandler = () => {
                        // Wait for viewport to settle after orientation change
                        setTimeout(() => {
                            requestAnimationFrame(this.updateStickyPosition);
                        }, 100);
                    };
                    
                    window.addEventListener('orientationchange', this.stickyOrientationHandler, { passive: true });
                    
                    this.updateStickyPosition();
                }
            }

            updateScrollSystem() {
                this.container.style.height = '';
                
                requestAnimationFrame(() => {
                    const containerHeight = this.container.clientHeight;
                    const scrollHeight = this.container.scrollHeight;
                    
                    this.totalScrollableHeight = scrollHeight - containerHeight;
                    this.scrollCount = Math.ceil(this.totalScrollableHeight / this.pixelsPerScroll);
                    
                    const hasScrollableContent = this.totalScrollableHeight > 1;
                    
                    if (this.navigationPanel) {
                        const shouldShow = hasScrollableContent && this.isContainerVisible();
                        this.navigationPanel.style.display = shouldShow ? 'flex' : 'none';
                    }
                    
                    this.updateIndicator();
                });
            }

            isContainerVisible() {
                const rect = this.container.getBoundingClientRect();
                const windowHeight = window.innerHeight;
                return rect.bottom > 0 && rect.top < windowHeight;
            }

            debouncedUpdateScrollSystem() {
                if (this.recalculationTimer) {
                    clearTimeout(this.recalculationTimer);
                }
                this.recalculationTimer = setTimeout(() => this.updateScrollSystem(), 100);
            }

            updateIndicator() {
                if (this.totalScrollableHeight <= 1) {
                    this.indicator.style.cssText = `height: ${this.indicatorsContainer.clientHeight}px; position: relative; top: 0px;`;
                    return;
                }
                
                const scrollProgress = Math.max(0, Math.min(1, this.container.scrollTop / this.totalScrollableHeight));
                const indicatorHeight = this.scrollCount > 0 ? this.indicatorsContainer.clientHeight / (this.scrollCount + 1) : this.indicatorsContainer.clientHeight;
                
                const availableSpace = this.indicatorsContainer.clientHeight - indicatorHeight;
                const indicatorTop = scrollProgress * availableSpace;
                const constrainedTop = Math.max(0, Math.min(availableSpace, indicatorTop));
                
                this.indicator.style.cssText = `height: ${Math.max(10, indicatorHeight)}px; position: relative; top: ${constrainedTop}px;`;
            }

            getScrollStepFromPosition(clientY) {
                const rect = this.indicatorsContainer.getBoundingClientRect();
                const clickY = Math.max(0, Math.min(this.indicatorsContainer.clientHeight, clientY - rect.top));
                const clickRatio = clickY / this.indicatorsContainer.clientHeight;
                
                const targetScrollTop = clickRatio * this.totalScrollableHeight;
                return Math.max(0, Math.min(this.totalScrollableHeight, targetScrollTop));
            }

            handleScrollUp() {
                this.container.scrollTop = Math.max(0, this.container.scrollTop - this.pixelsPerScroll);
                setTimeout(this.debouncedUpdateScrollSystem, 50);
            }
            
            handleScrollDown() {
                this.container.scrollTop = Math.min(this.totalScrollableHeight, this.container.scrollTop + this.pixelsPerScroll);
                setTimeout(this.debouncedUpdateScrollSystem, 50);
            }

            handleIndicatorClick(e) {
                if (e.target === this.indicator) return;
                
                const clientY = e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : 0);
                const targetScrollTop = this.getScrollStepFromPosition(clientY);
                
                this.container.scrollTop = targetScrollTop;
            }

            handleIndicatorDrag(e) {
                this.isDragging = true;
                e.preventDefault();
                
                const getClientY = (event) => {
                    return event.clientY || (event.touches && event.touches[0] ? event.touches[0].clientY : 0);
                };
                
                const handleMove = (e) => {
                    if (!this.isDragging) return;
                    e.preventDefault();
                    
                    const rect = this.indicatorsContainer.getBoundingClientRect();
                    const clientY = getClientY(e);
                    const mouseY = Math.max(0, Math.min(this.indicatorsContainer.clientHeight, clientY - rect.top));
                    const scrollRatio = mouseY / this.indicatorsContainer.clientHeight;
                    
                    this.container.scrollTop = Math.max(0, Math.min(this.totalScrollableHeight, scrollRatio * this.totalScrollableHeight));
                };
                
                const handleEnd = () => {
                    this.isDragging = false;
                    document.removeEventListener('mousemove', handleMove);
                    document.removeEventListener('mouseup', handleEnd);
                    document.removeEventListener('touchmove', handleMove);
                    document.removeEventListener('touchend', handleEnd);
                    
                    setTimeout(this.debouncedUpdateScrollSystem, 50);
                };
                
                document.addEventListener('mousemove', handleMove, { passive: false });
                document.addEventListener('mouseup', handleEnd);
                document.addEventListener('touchmove', handleMove, { passive: false });
                document.addEventListener('touchend', handleEnd);
            }

            updateStickyPosition() {
                const scrollPosition = parseInt(this.header.getAttribute('data-scroll-position') || '0', 10);
                const currentScrollState = scrollPosition === 0 ? 'top' : 'scrolled';
                
                // Only execute if scroll state changed (0 to non-0 or non-0 to 0)
                if (this.previousScrollState !== null && this.previousScrollState === currentScrollState) {
                    return;
                }
                
                this.previousScrollState = currentScrollState;
                
                if (window.innerWidth >= 750) {
                    const currentHeaderHeight = this.header.offsetHeight;
                    const viewportHeight = window.innerHeight;
                    const bottomSpacing = scrollPosition === 0 ? 42 : 10;
                    const availableHeight = viewportHeight - currentHeaderHeight - bottomSpacing;
                    
                    // Check if media list content is fully visible
                    const mediaListScrollHeight = this.mediaList.scrollHeight;
                    if (mediaListScrollHeight <= availableHeight) {
                        return;
                    }
                    
                    this.stickyColumn.style.top = `${currentHeaderHeight}px`;
                    
                    this.mediaList.style.height = `${availableHeight}px`;
                    this.mediaList.style.maxHeight = `${availableHeight}px`;
                    this.mediaList.style.overflowY = 'auto';
                    
                    requestAnimationFrame(this.debouncedUpdateScrollSystem);
                } else {
                    // Clear inline styles on mobile
                    if (this.mediaList) {
                        this.mediaList.style.height = '';
                        this.mediaList.style.maxHeight = '';
                        this.mediaList.style.overflowY = '';
                    }
                }
            }
            
            // Unified resize handler - handles both scroll system and sticky positioning
            handleResize() {
                clearTimeout(this.resizeTimeout);
                this.resizeTimeout = setTimeout(() => {
                    this.debouncedUpdateScrollSystem(); // Update scroll navigation
                    this.updateStickyPosition();        // Update sticky positioning
                }, 150);
            }
        }
    )
}